import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  Divider,
  Stack,
  Skeleton,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  MoreVert,
  Edit,
  Delete,
  LocalBar,
  Archive,
  CalendarToday,
  Comment
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { fetchBottle, archiveBottle } from '../../store/slices/bottlesSlice';
import { Bottle } from '../../../domain/entities';
import { BOTTLE_TYPES_LABELS } from '../../../core/config/constants';
import { RootState } from '../../store/store';

const BottleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { currentBottle, isLoading, error } = useAppSelector((state: RootState) => state.bottles);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDate, setArchiveDate] = useState<Date>(new Date());
  const [archiveComment, setArchiveComment] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchBottle(id));
    }
  }, [dispatch, id]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/bottles/${id}/edit`);
  };

  const handleDelete = () => {
    handleMenuClose();
    // TODO: Implement delete functionality
    console.log('Delete bottle');
  };

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!currentBottle) return;

    try {
      await dispatch(archiveBottle({
        reference: currentBottle.id,
        archiveData: {
          dateBu: archiveDate,
          archiveComment
        }
      })).unwrap();

      setSnackbarMessage('Bouteille archivée avec succès');
      setSnackbarOpen(true);
      setArchiveDialogOpen(false);
      
      // Refresh bottle data
      dispatch(fetchBottle(currentBottle.id));
    } catch (error) {
      setSnackbarMessage('Erreur lors de l\'archivage');
      setSnackbarOpen(true);
    }
  };

  const getWineTypeColor = (type: string) => {
    switch (type) {
      case 'RED': return '#722F37';
      case 'WHITE': return '#F7F4E9';
      case 'ROSE': return '#E8A4C0';
      case 'CHAMPAGNE': return '#F7E7CE';
      case 'SPIRITS': return '#8B4513';
      default: return '#722F37';
    }
  };

  const getWineTypeTextColor = (type: string) => {
    return type === 'WHITE' || type === 'CHAMPAGNE' ? '#000000' : '#FFFFFF';
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={300} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={60} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={150} />
      </Box>
    );
  }

  if (error || !currentBottle) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          Erreur lors du chargement de la bouteille
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Retour
        </Button>
      </Box>
    );
  }

  return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          backgroundColor: '#000000',
          maxWidth: { xs: '100%', sm: 450 },
          margin: '0 auto'
        }}
      >
        {/* Hero Image Section */}
        <Box
          sx={{
            position: 'relative',
            height: 300,
            backgroundColor: getWineTypeColor(currentBottle.type),
            backgroundImage: currentBottle.imageUrl ? `url(${currentBottle.imageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'flex-end',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              zIndex: 1
            }
          }}
        >
          {/* App Bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1,
              zIndex: 2
            }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: 'white' }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={handleMenuClick}
              sx={{ color: 'white' }}
            >
              <MoreVert />
            </IconButton>
          </Box>

          {/* Bottle Icon if no image */}
          {!currentBottle.imageUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1
              }}
            >
              <LocalBar sx={{ fontSize: 100, color: 'rgba(255,255,255,0.3)' }} />
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Bottle Header */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{ 
                color: '#722F37',
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              {currentBottle.name}
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={BOTTLE_TYPES_LABELS[currentBottle.type as keyof typeof BOTTLE_TYPES_LABELS]}
                sx={{
                  backgroundColor: getWineTypeColor(currentBottle.type),
                  color: getWineTypeTextColor(currentBottle.type),
                  fontWeight: 'bold'
                }}
              />
              {currentBottle.year && (
                <Chip
                  label={currentBottle.year}
                  sx={{
                    backgroundColor: '#800020',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={currentBottle.rating} readOnly />
              <Typography sx={{ color: '#FFFFFF' }}>
                {currentBottle.rating}/5
              </Typography>
            </Box>
          </Box>

          {/* Information Card */}
          <Card
            sx={{
              backgroundColor: '#1A1A1A',
              border: '1px solid #641835',
              borderRadius: 1,
              mb: 2
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 2 }}
              >
                Informations
              </Typography>
              
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#CCCCCC' }}>Type:</Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {BOTTLE_TYPES_LABELS[currentBottle.type as keyof typeof BOTTLE_TYPES_LABELS]}
                  </Typography>
                </Box>
                
                {currentBottle.year && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#CCCCCC' }}>Année:</Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {currentBottle.year}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#CCCCCC' }}>Note:</Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {currentBottle.rating}/5
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#CCCCCC' }}>Cadeau:</Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {currentBottle.isGift ? 'Oui' : 'Non'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#CCCCCC' }}>Mode privé:</Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {currentBottle.isPrivate ? 'Oui' : 'Non'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#CCCCCC' }}>Ajoutée le:</Typography>
                  <Typography sx={{ color: '#FFFFFF' }}>
                    {currentBottle.createdAt && !isNaN(currentBottle.createdAt.getTime()) 
                      ? format(currentBottle.createdAt, 'dd/MM/yyyy', { locale: fr })
                      : 'Date inconnue'}
                  </Typography>
                </Box>
                
                {currentBottle.archivedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#CCCCCC' }}>Archivée le:</Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {currentBottle.archivedAt && !isNaN(currentBottle.archivedAt.getTime())
                        ? format(currentBottle.archivedAt, 'dd/MM/yyyy', { locale: fr })
                        : 'Date inconnue'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Description Card */}
          {currentBottle.description && (
            <Card
              sx={{
                backgroundColor: '#1A1A1A',
                border: '1px solid #641835',
                borderRadius: 1,
                mb: 2
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 2 }}
                >
                  Description
                </Typography>
                <Typography sx={{ color: '#FFFFFF' }}>
                  {currentBottle.description}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Archive Comment Card */}
          {currentBottle.isArchived && currentBottle.archiveComment && (
            <Card
              sx={{
                backgroundColor: '#1A1A1A',
                border: '1px solid #641835',
                borderRadius: 1,
                mb: 2
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: '#FFFFFF', fontWeight: 'bold' }}
                  >
                    Commentaire de dégustation
                  </Typography>
                </Box>
                <Typography sx={{ color: '#FFFFFF', fontStyle: 'italic', mb: 1 }}>
                  {currentBottle.archiveComment}
                </Typography>
                {currentBottle.archivedAt && (
                  <Typography sx={{ color: '#CCCCCC', fontSize: '0.875rem' }}>
                    Dégustée le {currentBottle.archivedAt && !isNaN(currentBottle.archivedAt.getTime())
                      ? format(currentBottle.archivedAt, 'dd/MM/yyyy', { locale: fr })
                      : 'Date inconnue'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Archive Button */}
        {!currentBottle.isArchived && (
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<LocalBar />}
              onClick={handleArchiveClick}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              J'ai bu cette bouteille
            </Button>
          </Box>
        )}

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <Edit sx={{ mr: 1 }} />
            Modifier
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Archive Dialog */}
        <Dialog
          open={archiveDialogOpen}
          onClose={() => setArchiveDialogOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF'
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <Archive sx={{ mr: 1 }} />
            Archiver {currentBottle.name}
          </DialogTitle>
          <DialogContent>
            <Typography 
              variant="body2" 
              sx={{ color: '#FFFFFF', mb: 3, fontStyle: 'italic' }}
            >
              Cette bouteille sera marquée comme bue et archivée.
            </Typography>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Date de dégustation"
                value={archiveDate.toISOString().split('T')[0]}
                onChange={(e) => setArchiveDate(new Date(e.target.value))}
                sx={{ 
                  mb: 2, 
                  '& .MuiInputLabel-root': { color: '#FFFFFF' },
                  '& .MuiOutlinedInput-root': { 
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#FFFFFF' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Commentaire (optionnel)"
                value={archiveComment}
                onChange={(e) => setArchiveComment(e.target.value)}
                sx={{
                  '& .MuiInputLabel-root': { color: '#FFFFFF' },
                  '& .MuiOutlinedInput-root': { 
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#FFFFFF' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setArchiveDialogOpen(false)}
              sx={{ color: '#FFFFFF' }}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleArchiveConfirm}
              variant="contained"
              sx={{ backgroundColor: '#641835' }}
            >
              Archiver
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="success">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default BottleDetailPage;